import React, { useEffect, useState } from "react";
import { BarChart, Bar, XAxis, YAxis, ResponsiveContainer, Tooltip, Cell } from "recharts";
import { ChartBar, Robot } from "@phosphor-icons/react";
import { Link } from "react-router-dom";
import { PageLoader, Stat, EmptyState } from "../../components/shared";
import { Button } from "../../components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import api, { money } from "../../lib/api";

const COLORS = ["#E63946", "#1D3557", "#2A9D8F", "#F4A261", "#457B9D"];

export default function Analytics() {
  const [events, setEvents] = useState([]);
  const [eventId, setEventId] = useState("all");
  const [a, setA] = useState(null);

  useEffect(() => { api.get("/organizer/events").then((r) => setEvents(r.data.events || [])); }, []);
  useEffect(() => {
    setA(null);
    const url = eventId === "all" ? "/organizer/analytics" : `/organizer/analytics?event_id=${eventId}`;
    api.get(url).then((r) => setA(r.data));
  }, [eventId]);

  const g = a?.gated;

  return (
    <div>
      <div className="flex items-center justify-between mb-6 gap-3 flex-wrap">
        <div><div className="overline mb-1">Insights</div><h1 className="font-heading text-3xl md:text-4xl font-semibold tracking-tight">Analytics</h1></div>
        <div className="flex items-center gap-2">
          <Select value={eventId} onValueChange={setEventId}>
            <SelectTrigger className="w-56" data-testid="analytics-event-select"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All events</SelectItem>
              {events.map((e) => <SelectItem key={e.id} value={e.id} data-testid={`analytics-ev-${e.id}`}>{e.title}</SelectItem>)}
            </SelectContent>
          </Select>
          <Button asChild variant="outline" className="rounded-full border-primary/30 text-primary"><Link to="/organizer/copilot"><Robot className="mr-1.5" /> Explain with AI</Link></Button>
        </div>
      </div>

      {!a ? <PageLoader /> : (
        <>
          <div className="grid grid-cols-2 lg:grid-cols-5 gap-4 mb-8">
            <Stat label="Revenue" value={money(a.revenue)} accent />
            <Stat label="Tickets Sold" value={a.tickets_sold} />
            <Stat label="Capacity %" value={`${a.capacity_pct}%`} />
            <Stat label="Refunds" value={a.refunds} />
            <Stat label="Check-ins" value={a.checkins} />
          </div>

          {g && (
            <div className="rounded-xl border border-border bg-card p-6 mb-8" data-testid="gated-funnel">
              <h2 className="font-heading text-lg font-semibold mb-1">Guest funnel <span className="text-sm font-normal text-muted-foreground capitalize">· {String(g.mode).replace("_", " ")} event</span></h2>
              {g.mode === "approval" ? (
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mt-3">
                  <Stat label="Requests" value={g.approvals ? Object.values(g.approvals).reduce((s, v) => s + v, 0) : 0} />
                  <Stat label="Pending" value={g.pending_approvals} />
                  <Stat label="Approved" value={g.approved} />
                  <Stat label="Paid / ticketed" value={g.paid} accent />
                </div>
              ) : (
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mt-3">
                  <Stat label="Invited" value={g.invited} />
                  <Stat label="Responded" value={g.responded} />
                  <Stat label="Expected attendance" value={g.expected_attendance} />
                  <Stat label="Passes issued" value={g.guest_tickets} accent />
                </div>
              )}
              {g.response_counts && Object.keys(g.response_counts).length > 0 && (
                <div className="flex flex-wrap gap-2 mt-4">
                  {Object.entries(g.response_counts).map(([k, v]) => <span key={k} className="px-3 py-1.5 rounded-full bg-muted text-sm font-medium capitalize">{k.replace(/_/g, " ")}: {v}</span>)}
                </div>
              )}
            </div>
          )}

          <div className="rounded-xl border border-border bg-card p-6">
            <h2 className="font-heading text-lg font-semibold mb-4">Ticket category performance</h2>
            {a.category_performance.length === 0 ? (
              <EmptyState icon={ChartBar} title="No sales yet" subtitle="Charts appear once tickets start selling." testid="no-analytics" />
            ) : (
              <div className="h-72">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={a.category_performance} margin={{ left: 10, right: 10 }}>
                    <XAxis dataKey="name" tick={{ fontSize: 12 }} />
                    <YAxis tick={{ fontSize: 12 }} />
                    <Tooltip formatter={(v, k) => (k === "revenue" ? money(v) : v)} />
                    <Bar dataKey="revenue" radius={[6, 6, 0, 0]}>
                      {a.category_performance.map((_, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
}
